import React, { useState, useEffect } from 'react';
import { Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ACCORDION_CLASS_NAME } from '../../constants/mui-components';
import { BlackLine, FillLineInfinite } from '../lines';

function SingleExpandAccordion({ accordionsInfo, onPanelChange }) {
  const [expanded, setExpanded] = useState('panel1');
  const [animationKey, setAnimationKey] = useState(0);
  const theme = useTheme();
  const itemDuration = 9;

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    setAnimationKey((prevKey) => prevKey + 1);
    onPanelChange(panel);
  };

  useEffect(() => {
    if (expanded) {
      const timer = setTimeout(() => {
        const currentIndex = accordionsInfo.findIndex((acc) => acc.name === expanded);
        const nextIndex = (currentIndex + 1) % accordionsInfo.length;
        const nextPanel = accordionsInfo[nextIndex].name;
        setExpanded(nextPanel);
        onPanelChange(nextPanel);
        setAnimationKey((prevKey) => prevKey + 1);
      }, itemDuration * 1000);
      return () => clearTimeout(timer);
    }
    return null;
  }, [expanded, accordionsInfo]);

  return (
    <Stack sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      width: '100%',
      maxWidth: '45rem',
      height: '100%',
    }}
    >
      {accordionsInfo.map((accordion) => (
        <Accordion
          expanded={expanded === accordion.name}
          onChange={handleChange(accordion.name)}
          elevation={0}
          disableGutters
          className={ACCORDION_CLASS_NAME}
        >
          <Collapse in={expanded !== accordion.name} timeout="auto" unmountOnExit>
            <AccordionSummary
              aria-controls="panel1bh-content"
              id="panel1bh-header"
              disableGutters
              sx={{
                display: 'flex',
                padding: '3rem 0 0.5rem 0',
                width: '100%',
                flexDirection: 'column',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
                backgroundColor: 'transparent',
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem',
                width: '100%',
              }}
              >
                <Typography variant="medium_1" color={theme.palette.neutral.A500}>
                  {accordion.text}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: '0.0625rem',
                    flexShrink: 0,
                    background: theme.palette.neutral.A500,
                  }}
                />
              </Box>
            </AccordionSummary>
          </Collapse>
          <AccordionDetails sx={{
            display: 'flex',
            padding: '3rem 0 0.5rem 0',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '1.5rem',
            alignSelf: 'stretch',
            backgroundColor: 'transparent',
          }}
          >
            <Typography variant="medium_3" color="white">
              {accordion.text}
            </Typography>
            <BlackLine>
              <FillLineInfinite
                color={theme.palette.primary.A700}
                key={animationKey}
                seconds={itemDuration}
              />
            </BlackLine>
            <Typography variant="medium_1" color="white">
              {accordion.subtext}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

SingleExpandAccordion.propTypes = {
  accordionsInfo: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    text: PropTypes.string,
    subtext: PropTypes.string,
    video: PropTypes.string,
  })).isRequired,
  onPanelChange: PropTypes.func.isRequired,
};

export default SingleExpandAccordion;
